<template>
    <div
        class="almighty-mention-editor"
        :class="[
            `almighty-mention-editor--${variant}`,
            {
                'is-focused': isFocused,
                'is-disabled': disabled,
            },
        ]"
        @click="focusEditor"
    >
        <div
            ref="editorRef"
            class="almighty-mention-editor__content"
            :data-empty="isPlaceholderVisible ? 'true' : 'false'"
            :contenteditable="disabled ? 'false' : 'true'"
            :tabindex="disabled ? -1 : 0"
            role="textbox"
            aria-multiline="true"
            @mousedown="handleMouseDown"
            @input="handleInput"
            @keydown="handleKeydown"
            @paste="handlePaste"
            @focus="handleFocus"
            @blur="handleBlur"
            @mouseup="handleSelectionChange"
            @compositionstart="isComposing = true"
            @compositionend="handleCompositionEnd"
        ></div>
        <span
            v-if="isPlaceholderVisible"
            class="almighty-mention-editor__placeholder"
            aria-hidden="true"
        >
            {{ placeholder }}
        </span>
        <span
            v-if="props.showCount && hasMaxLength"
            class="almighty-mention-editor__count"
            :class="{
                'is-near-limit': isNearLimit && !isAtLimit,
                'is-at-limit': isAtLimit,
            }"
            aria-hidden="true"
        >
            {{ characterCount }}/{{ props.maxLength }}
        </span>

        <ClientOnly>
            <Teleport to="body">
                <div
                    v-if="shouldShowMentionMenu"
                    ref="mentionMenuRootRef"
                    class="almighty-mention-menu fixed z-[9999]"
                    :class="{ 'workbench-almighty-mention-menu': variant === 'workbench' }"
                    :style="mentionMenuStyle"
                    @pointerdown.capture="onMentionMenuPointerDown"
                    @wheel.stop
                >
                    <div
                        ref="mentionMenuScrollRef"
                        class="almighty-mention-menu__scroll"
                        @wheel.stop
                    >
                        <button
                            v-for="(asset, index) in filteredAssets"
                            :key="asset.id"
                            type="button"
                            class="almighty-mention-menu__item"
                            :class="{ 'is-active': index === activeMentionOptionIndex }"
                            @mousedown.prevent="handleSelectMention(asset)"
                        >
                            <span class="almighty-mention-menu__thumb">
                                <img
                                    v-if="asset.previewUrl && asset.kind === 'image'"
                                    :src="asset.previewUrl"
                                    :alt="asset.label"
                                    class="almighty-mention-menu__preview"
                                />
                                <video
                                    v-else-if="asset.previewUrl && asset.kind === 'video'"
                                    :src="asset.previewUrl"
                                    class="almighty-mention-menu__preview"
                                    muted
                                    playsinline
                                    preload="metadata"
                                ></video>
                                <span
                                    v-else
                                    class="almighty-mention-menu__audio"
                                >
                                    {{ getAudioBadge(asset.kind) }}
                                </span>
                            </span>
                            <span class="almighty-mention-menu__label">
                                {{ asset.label }}
                            </span>
                        </button>
                    </div>
                </div>
            </Teleport>
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import type {
    AlmightyMentionAsset,
    AlmightyPromptMentionToken,
} from '~/utils/almightyMention'
import {
    cloneAlmightyPromptMentionTokens,
    normalizeAlmightyPromptMentionTokens,
    resolveAlmightyMentionContext,
    serializeAlmightyPromptMentionDisplayTokens,
    serializeAlmightyPromptMentionTokens,
} from '~/utils/almightyMention'

const props = withDefaults(
    defineProps<{
        assets?: AlmightyMentionAsset[]
        maxLength?: number
        placeholder?: string
        disabled?: boolean
        variant?: 'setting' | 'workbench'
        showCount?: boolean
    }>(),
    {
        assets: () => [],
        placeholder: '',
        disabled: false,
        variant: 'setting',
        showCount: true,
    }
)

const prompt = defineModel<string>('prompt', {
    default: '',
})

const tokens = defineModel<AlmightyPromptMentionToken[]>('tokens', {
    default: () => [],
})

const editorRef = ref<HTMLDivElement | null>(null)
const isFocused = ref(false)
const isComposing = ref(false)
const pendingAssetSync = ref(false)
const mentionState = ref<{
    query: string
    start: number
    end: number
    activeOption: number
} | null>(null)
const mentionMenuPosition = ref({
    top: 0,
    left: 0,
    width: 240,
    scrollMaxHeight: 320,
})
const mentionMenuRootRef = ref<HTMLDivElement | null>(null)
const mentionMenuScrollRef = ref<HTMLDivElement | null>(null)

/** 在下拉（含滚动条）上按下时，避免 contenteditable blur 误关菜单 */
const MENTION_MENU_BLUR_GUARD_MS = 500
const mentionMenuBlurGuardUntil = ref(0)

const onMentionMenuPointerDown = () => {
    mentionMenuBlurGuardUntil.value = Date.now() + MENTION_MENU_BLUR_GUARD_MS
}

type EditorSelectionSnapshot = {
    wasFocused: boolean
    isCollapsed: boolean
    startOffset: number
    endOffset: number
    boundaryOffset: number | null
}

const assetMap = computed(() => {
    return new Map((props.assets || []).map(asset => [asset.id, asset]))
})

const normalizedTokens = computed(() =>
    normalizeAlmightyPromptMentionTokens(tokens.value, prompt.value)
)

const getCharWeight = (char: string) => {
    const codePoint = char.codePointAt(0) ?? 0

    if (codePoint <= 0x00ff) return 1
    if (codePoint >= 0xff61 && codePoint <= 0xff9f) return 1

    return 2
}

const getWeightedCharLength = (value: string) => {
    let total = 0
    for (const char of value) {
        total += getCharWeight(char)
    }
    return total
}

const sliceByWeightedLength = (value: string, maxLength: number) => {
    if (maxLength <= 0) return ''
    let consumed = 0
    let result = ''
    for (const char of value) {
        const charWeight = getCharWeight(char)
        if (consumed + charWeight > maxLength) break
        consumed += charWeight
        result += char
    }
    return result
}

const characterCount = computed(() =>
    getWeightedCharLength(
        serializeAlmightyPromptMentionTokens(normalizedTokens.value, props.assets)
    )
)
const hasMaxLength = computed(
    () => typeof props.maxLength === 'number' && Number.isFinite(props.maxLength) && props.maxLength > 0
)
const isNearLimit = computed(() =>
    hasMaxLength.value && characterCount.value > (props.maxLength as number) * 0.8
)
const isAtLimit = computed(() =>
    hasMaxLength.value && characterCount.value >= (props.maxLength as number)
)

const getMeaningfulPlainText = (value: string) =>
    value
        .replace(/\u00a0/g, ' ')
        .replace(/[\u200b\u200c\u200d\ufeff]/g, '')
        .trim()

const hasMeaningfulTokenContent = (nextTokens: AlmightyPromptMentionToken[]) =>
    nextTokens.some(token =>
        token.type === 'mention' || getMeaningfulPlainText(token.text).length > 0
    )

const hasMentionToken = (nextTokens: AlmightyPromptMentionToken[]) =>
    nextTokens.some(token => token.type === 'mention')

const isPlaceholderVisible = computed(() =>
    !props.disabled &&
    !isFocused.value &&
    !hasMeaningfulTokenContent(normalizedTokens.value)
)

const filteredAssets = computed(() => {
    if (!mentionState.value) return []
    const query = mentionState.value.query.trim().toLowerCase()
    if (!query) return props.assets || []
    return (props.assets || []).filter(asset =>
        asset.label.toLowerCase().includes(query)
    )
})

const activeMentionOptionIndex = computed(() => {
    if (!mentionState.value) return -1
    const maxIndex = Math.max(filteredAssets.value.length - 1, 0)
    return Math.min(Math.max(mentionState.value.activeOption, 0), maxIndex)
})

const shouldShowMentionMenu = computed(() =>
    !props.disabled &&
    !!mentionState.value &&
    filteredAssets.value.length > 0
)

const mentionMenuStyle = computed(() => ({
    top: `${mentionMenuPosition.value.top}px`,
    left: `${mentionMenuPosition.value.left}px`,
    width: `${mentionMenuPosition.value.width}px`,
    '--almighty-mention-menu-max-height': `${mentionMenuPosition.value.scrollMaxHeight}px`,
}))

/** 仅在列表容器内滚动，禁止 scrollIntoView（会滚动整页并触发 document scroll 监听 → 误关菜单） */
const scrollActiveMentionItemIntoView = () => {
    if (!shouldShowMentionMenu.value) return
    nextTick(() => {
        const scrollEl = mentionMenuScrollRef.value
        const el = scrollEl?.querySelector(
            '.almighty-mention-menu__item.is-active'
        ) as HTMLElement | null
        if (!scrollEl || !el) return
        const cRect = scrollEl.getBoundingClientRect()
        const eRect = el.getBoundingClientRect()
        if (eRect.top < cRect.top) {
            scrollEl.scrollTop += eRect.top - cRect.top
        } else if (eRect.bottom > cRect.bottom) {
            scrollEl.scrollTop += eRect.bottom - cRect.bottom
        }
    })
}

watch(
    () => [activeMentionOptionIndex.value, shouldShowMentionMenu.value] as const,
    () => {
        scrollActiveMentionItemIntoView()
    }
)

const getEditorElement = () => editorRef.value as HTMLDivElement | null

const focusEditor = () => {
    if (props.disabled) return
    nextTick(() => {
        getEditorElement()?.focus()
    })
}

const getTokenDisplayText = (token: AlmightyPromptMentionToken) => {
    if (token.type === 'text') return token.text
    return assetMap.value.get(token.assetId)?.label || token.fallbackLabel
}

const getAudioBadge = (kind: string) => {
    if (kind === 'audio') return 'A'
    return ''
}

const createMentionChip = (token: Extract<AlmightyPromptMentionToken, { type: 'mention' }>) => {
    const asset = assetMap.value.get(token.assetId)
    const label = asset?.label || token.fallbackLabel
    const chip = document.createElement('span')
    chip.contentEditable = 'false'
    chip.className = 'almighty-mention-chip'
    chip.dataset.assetId = token.assetId
    chip.dataset.kind = token.kind
    chip.dataset.fallbackLabel = token.fallbackLabel
    chip.dataset.displayText = label

    const thumb = document.createElement('span')
    thumb.className = 'almighty-mention-chip__thumb'

    if (asset?.previewUrl && asset.kind === 'image') {
        const preview = document.createElement('img')
        preview.src = asset.previewUrl
        preview.alt = label
        preview.className = 'almighty-mention-chip__preview'
        thumb.append(preview)
    } else if (asset?.previewUrl && asset.kind === 'video') {
        const preview = document.createElement('video')
        preview.src = asset.previewUrl
        preview.className = 'almighty-mention-chip__preview'
        preview.muted = true
        preview.playsInline = true
        preview.preload = 'metadata'
        thumb.append(preview)
    } else {
        const icon = document.createElement('span')
        icon.className = 'almighty-mention-chip__audio'
        icon.textContent = getAudioBadge(token.kind)
        thumb.append(icon)
    }

    const text = document.createElement('span')
    text.className = 'almighty-mention-chip__label'
    text.textContent = label

    chip.append(thumb, text)
    return chip
}

const getNodeDisplayLength = (node: Node): number => {
    if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent || '').replace(/\u00a0/g, ' ').length
    }

    if (!(node instanceof HTMLElement)) return 0

    if (node.dataset.displayText) {
        return node.dataset.displayText.length
    }

    if (node.tagName === 'BR') return 1

    return Array.from(node.childNodes).reduce(
        (total, child) => total + getNodeDisplayLength(child),
        0
    )
}

const getNodeDisplayWeightedLength = (node: Node): number => {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = (node.textContent || '').replace(/\u00a0/g, ' ')
        return getWeightedCharLength(text)
    }

    if (!(node instanceof HTMLElement)) return 0

    if (node.dataset.displayText) {
        return getWeightedCharLength(node.dataset.displayText)
    }

    if (node.tagName === 'BR') return 1

    return Array.from(node.childNodes).reduce(
        (total, child) => total + getNodeDisplayWeightedLength(child),
        0
    )
}

const parseNodeTokens = (node: Node): AlmightyPromptMentionToken[] => {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = (node.textContent || '').replace(/\u00a0/g, ' ')
        return text ? [{ type: 'text', text }] : []
    }

    if (!(node instanceof HTMLElement)) return []

    if (node.dataset.assetId) {
        return [{
            type: 'mention',
            assetId: node.dataset.assetId,
            kind: (node.dataset.kind || 'image') as any,
            fallbackLabel: node.dataset.fallbackLabel || node.dataset.displayText || '',
        }]
    }

    if (node.tagName === 'BR') {
        return [{ type: 'text', text: '\n' }]
    }

    return Array.from(node.childNodes).flatMap(parseNodeTokens)
}

const normalizeEditorTokens = (nextTokens: AlmightyPromptMentionToken[]) => {
    const normalized = cloneAlmightyPromptMentionTokens(nextTokens)
    if (!hasMeaningfulTokenContent(normalized)) {
        return []
    }

    return normalized
}

const parseEditorTokens = (editor: HTMLElement) =>
    normalizeEditorTokens(
        Array.from(editor.childNodes).flatMap(parseNodeTokens)
    )

const renderTokensToEditor = (editor: HTMLDivElement, nextTokens: AlmightyPromptMentionToken[]) => {
    editor.innerHTML = ''

    const normalized = cloneAlmightyPromptMentionTokens(nextTokens)
    for (const token of normalized) {
        if (token.type === 'text') {
            editor.append(document.createTextNode(token.text))
        } else {
            editor.append(createMentionChip(token))
        }
    }

    editor.dataset.empty = hasMeaningfulTokenContent(normalized) ? 'false' : 'true'
}

const truncateTokensToMaxLength = (
    nextTokens: AlmightyPromptMentionToken[],
    maxLength: number
) => {
    const normalized = cloneAlmightyPromptMentionTokens(nextTokens)
    const truncated: AlmightyPromptMentionToken[] = []
    let remaining = Math.max(0, maxLength)

    for (const token of normalized) {
        if (remaining <= 0) break

        const displayText = getTokenDisplayText(token)
        if (!displayText) continue

        if (token.type === 'mention') {
            const mentionLength = getWeightedCharLength(displayText)
            if (mentionLength > remaining) break
            truncated.push(token)
            remaining -= mentionLength
            continue
        }

        const nextText = sliceByWeightedLength(displayText, remaining)
        if (!nextText) continue
        truncated.push({
            type: 'text',
            text: nextText,
        })
        remaining -= getWeightedCharLength(nextText)
    }

    return cloneAlmightyPromptMentionTokens(truncated)
}

const setCaretAtEditorEnd = (editor: HTMLDivElement) => {
    const selection = window.getSelection()
    if (!selection) return
    const range = document.createRange()
    range.selectNodeContents(editor)
    range.collapse(false)
    selection.removeAllRanges()
    selection.addRange(range)
}

const setCaretByTextOffset = (editor: HTMLDivElement, offset: number) => {
    const selection = window.getSelection()
    if (!selection) return

    let remaining = Math.max(0, offset)
    const range = document.createRange()

    const walk = (node: Node): boolean => {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = (node.textContent || '').replace(/\u00a0/g, ' ')
            if (remaining <= text.length) {
                range.setStart(node, remaining)
                range.collapse(true)
                return true
            }
            remaining -= text.length
            return false
        }

        if (!(node instanceof HTMLElement)) return false

        if (node.dataset.displayText) {
            if (remaining <= node.dataset.displayText.length) {
                range.setStartAfter(node)
                range.collapse(true)
                return true
            }
            remaining -= node.dataset.displayText.length
            return false
        }

        if (node.tagName === 'BR') {
            if (remaining <= 1) {
                range.setStartBefore(node)
                range.collapse(true)
                return true
            }
            remaining -= 1
            return false
        }

        return Array.from(node.childNodes).some(walk)
    }

    const found = Array.from(editor.childNodes).some(walk)
    if (!found) {
        setCaretAtEditorEnd(editor)
        return
    }

    selection.removeAllRanges()
    selection.addRange(range)
}

const getSelectionRangeInsideEditor = (editor: HTMLDivElement) => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return null
    const range = selection.getRangeAt(0)
    if (!editor.contains(range.startContainer)) return null
    return range
}

const getRangeBeforeCaret = (editor: HTMLDivElement) => {
    const range = getSelectionRangeInsideEditor(editor)
    if (!range) return null
    const preCaretRange = range.cloneRange()
    preCaretRange.selectNodeContents(editor)
    preCaretRange.setEnd(range.startContainer, range.startOffset)
    return preCaretRange
}

const getSelectionContentLength = (editor: HTMLDivElement) => {
    const range = getSelectionRangeInsideEditor(editor)
    if (!range || range.collapsed) return 0
    return Array.from(range.cloneContents().childNodes).reduce(
        (total, node) => total + getNodeDisplayWeightedLength(node),
        0
    )
}

const getCaretOffset = (editor: HTMLDivElement) => {
    const range = getRangeBeforeCaret(editor)
    if (!range) {
        return parseEditorTokens(editor).reduce(
            (total, token) => total + getTokenDisplayText(token).length,
            0
        )
    }

    return Array.from(range.cloneContents().childNodes).reduce(
        (total, node) => total + getNodeDisplayLength(node),
        0
    )
}

const getOffsetFromRangePoint = (
    editor: HTMLDivElement,
    container: Node,
    offset: number
) => {
    try {
        const range = document.createRange()
        range.selectNodeContents(editor)
        range.setEnd(container, offset)
        return Array.from(range.cloneContents().childNodes).reduce(
            (total, node) => total + getNodeDisplayLength(node),
            0
        )
    } catch {
        return null
    }
}

const captureSelectionSnapshot = (editor: HTMLDivElement): EditorSelectionSnapshot | null => {
    const range = getSelectionRangeInsideEditor(editor)
    const wasFocused = document.activeElement === editor
    if (!range) {
        if (!wasFocused) return null
        const caretOffset = getCaretOffset(editor)
        return {
            wasFocused,
            isCollapsed: true,
            startOffset: caretOffset,
            endOffset: caretOffset,
            boundaryOffset: getCaretBoundaryOffset(editor),
        }
    }

    const startOffset =
        getOffsetFromRangePoint(editor, range.startContainer, range.startOffset) ??
        getCaretOffset(editor)
    const endOffset = range.collapsed
        ? startOffset
        : (
            getOffsetFromRangePoint(editor, range.endContainer, range.endOffset) ??
            startOffset
        )

    return {
        wasFocused,
        isCollapsed: range.collapsed,
        startOffset,
        endOffset,
        boundaryOffset: range.collapsed ? getCaretBoundaryOffset(editor) : null,
    }
}

const restoreSelectionSnapshot = (
    editor: HTMLDivElement,
    snapshot: EditorSelectionSnapshot | null
) => {
    if (!snapshot?.wasFocused) return

    editor.focus()

    if (snapshot.isCollapsed && snapshot.boundaryOffset != null) {
        setCaretAtEditorBoundary(editor, snapshot.boundaryOffset)
        updateMentionState(editor)
        return
    }

    const selection = window.getSelection()
    if (!selection) return

    if (!snapshot.isCollapsed) {
        const range = createRangeFromOffsets(
            editor,
            snapshot.startOffset,
            snapshot.endOffset
        )
        selection.removeAllRanges()
        selection.addRange(range)
        updateMentionState(editor)
        return
    }

    setCaretByTextOffset(editor, snapshot.startOffset)
    updateMentionState(editor)
}

const getCaretRect = (editor: HTMLDivElement) => {
    const range = getSelectionRangeInsideEditor(editor)
    if (!range) return null

    const collapsedRange = range.cloneRange()
    collapsedRange.collapse(true)
    let rect = collapsedRange.getBoundingClientRect()

    if (rect.width || rect.height) return rect

    const marker = document.createElement('span')
    marker.textContent = '\u200b'
    marker.style.display = 'inline-block'
    marker.style.width = '1px'
    marker.style.height = '1em'
    marker.style.pointerEvents = 'none'

    collapsedRange.insertNode(marker)
    rect = marker.getBoundingClientRect()
    marker.parentNode?.removeChild(marker)

    const selection = window.getSelection()
    if (selection) {
        selection.removeAllRanges()
        selection.addRange(collapsedRange)
    }

    return rect
}

const createRangeFromOffsets = (
    editor: HTMLDivElement,
    startOffset: number,
    endOffset: number
) => {
    const range = document.createRange()
    let currentOffset = 0
    let startSet = false
    let endSet = false

    const walk = (node: Node) => {
        if (endSet) return

        if (node.nodeType === Node.TEXT_NODE) {
            const text = (node.textContent || '').replace(/\u00a0/g, ' ')
            const nextOffset = currentOffset + text.length

            if (!startSet && startOffset <= nextOffset) {
                range.setStart(node, Math.max(0, startOffset - currentOffset))
                startSet = true
            }

            if (startSet && endOffset <= nextOffset) {
                range.setEnd(node, Math.max(0, endOffset - currentOffset))
                endSet = true
                return
            }

            currentOffset = nextOffset
            return
        }

        if (!(node instanceof HTMLElement)) return

        if (node.dataset.displayText) {
            const displayLength = node.dataset.displayText.length
            const nextOffset = currentOffset + displayLength

            if (!startSet && startOffset <= nextOffset) {
                range.setStartBefore(node)
                startSet = true
            }

            if (startSet && endOffset <= nextOffset) {
                range.setEndAfter(node)
                endSet = true
                return
            }

            currentOffset = nextOffset
            return
        }

        if (node.tagName === 'BR') {
            const nextOffset = currentOffset + 1

            if (!startSet && startOffset <= nextOffset) {
                range.setStartBefore(node)
                startSet = true
            }

            if (startSet && endOffset <= nextOffset) {
                range.setEndAfter(node)
                endSet = true
                return
            }

            currentOffset = nextOffset
            return
        }

        Array.from(node.childNodes).forEach(walk)
    }

    Array.from(editor.childNodes).forEach(walk)

    if (!startSet) {
        range.selectNodeContents(editor)
        range.collapse(false)
    }
    if (!endSet) {
        range.setEndAfter(editor.lastChild || editor)
    }

    return range
}

const isMentionChipNode = (node: Node | null): node is HTMLElement =>
    node instanceof HTMLElement && !!node.dataset.assetId

const setCaretAtEditorBoundary = (editor: HTMLDivElement, boundaryOffset: number) => {
    const selection = window.getSelection()
    if (!selection) return

    const safeBoundaryOffset = Math.max(
        0,
        Math.min(boundaryOffset, editor.childNodes.length)
    )
    const range = document.createRange()
    range.setStart(editor, safeBoundaryOffset)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
}

const getCaretBoundaryOffset = (editor: HTMLDivElement) => {
    const range = getSelectionRangeInsideEditor(editor)
    if (!range || !range.collapsed) return null

    const { startContainer, startOffset } = range

    if (startContainer === editor) {
        return startOffset
    }

    let directChild: Node | null = startContainer
    while (directChild && directChild.parentNode !== editor) {
        directChild = directChild.parentNode
    }

    if (!directChild || directChild.parentNode !== editor) return null

    const childIndex = Array.from(editor.childNodes).indexOf(directChild as ChildNode)
    if (childIndex < 0) return null

    if (directChild.nodeType === Node.TEXT_NODE) {
        const textLength = (directChild.textContent || '').length
        if (startOffset === 0) return childIndex
        if (startOffset === textLength) return childIndex + 1
        return null
    }

    if (startContainer === directChild) {
        if (startOffset === 0) return childIndex
        if (startOffset === directChild.childNodes.length) return childIndex + 1
    }

    return null
}

const moveCaretAcrossMentionChip = (
    editor: HTMLDivElement,
    direction: 'left' | 'right'
) => {
    const boundaryOffset = getCaretBoundaryOffset(editor)
    if (boundaryOffset === null) return false

    const targetIndex = direction === 'left' ? boundaryOffset - 1 : boundaryOffset
    const adjacentNode = editor.childNodes[targetIndex] || null
    if (!isMentionChipNode(adjacentNode)) return false

    const nextBoundaryOffset = direction === 'left' ? targetIndex : targetIndex + 1
    setCaretAtEditorBoundary(editor, nextBoundaryOffset)
    updateMentionState(editor)
    return true
}

const removeAdjacentMentionChip = (
    editor: HTMLDivElement,
    direction: 'backward' | 'forward'
) => {
    const boundaryOffset = getCaretBoundaryOffset(editor)
    if (boundaryOffset === null) return false

    const targetIndex = direction === 'backward' ? boundaryOffset - 1 : boundaryOffset
    const adjacentNode = editor.childNodes[targetIndex] || null
    if (!isMentionChipNode(adjacentNode)) return false

    adjacentNode.remove()
    setCaretAtEditorBoundary(editor, targetIndex)
    syncModelsFromEditor(editor)
    updateMentionState(editor)
    return true
}

const handleMouseDown = (event: MouseEvent) => {
    if (props.disabled || event.button !== 0) return

    const editor = getEditorElement()
    const target = event.target
    if (!editor || !(target instanceof HTMLElement)) return

    const chip = target.closest('.almighty-mention-chip')
    if (!(chip instanceof HTMLElement) || chip.parentNode !== editor) return

    event.preventDefault()
    editor.focus()

    const chipIndex = Array.from(editor.childNodes).indexOf(chip as ChildNode)
    if (chipIndex < 0) return

    const rect = chip.getBoundingClientRect()
    const placeBefore = event.clientX <= rect.left + rect.width / 2
    setCaretAtEditorBoundary(editor, placeBefore ? chipIndex : chipIndex + 1)
    updateMentionState(editor)
}

const syncModelsFromEditor = (editor: HTMLDivElement) => {
    const nextTokens = parseEditorTokens(editor)
    const nextPrompt = serializeAlmightyPromptMentionTokens(nextTokens, props.assets)
    tokens.value = cloneAlmightyPromptMentionTokens(nextTokens)
    prompt.value = nextPrompt
    editor.dataset.empty = hasMeaningfulTokenContent(nextTokens) ? 'false' : 'true'
}

const syncEditorStateAfterMutation = (editor: HTMLDivElement) => {
    let nextTokens = parseEditorTokens(editor)
    let nextPrompt = serializeAlmightyPromptMentionTokens(nextTokens, props.assets)

    const maxLength = props.maxLength
    if (
        typeof maxLength === 'number' &&
        Number.isFinite(maxLength) &&
        maxLength > 0 &&
        getWeightedCharLength(nextPrompt) > maxLength
    ) {
        nextTokens = truncateTokensToMaxLength(nextTokens, maxLength)
        nextPrompt = serializeAlmightyPromptMentionTokens(nextTokens, props.assets)
        renderTokensToEditor(editor, nextTokens)
        nextTick(() => {
            editor.focus()
            setCaretAtEditorEnd(editor)
            updateMentionState(editor)
        })
    } else {
        editor.dataset.empty = hasMeaningfulTokenContent(nextTokens) ? 'false' : 'true'
        updateMentionState(editor)
    }

    tokens.value = cloneAlmightyPromptMentionTokens(nextTokens)
    prompt.value = nextPrompt
}

const updateMentionMenuPosition = (editor: HTMLDivElement) => {
    const caretRect = getCaretRect(editor)
    const editorRect = editor.getBoundingClientRect()
    const visualViewport = window.visualViewport
    const viewportLeft = visualViewport?.offsetLeft ?? 0
    const viewportTop = visualViewport?.offsetTop ?? 0
    const viewportWidth = visualViewport?.width ?? window.innerWidth
    const viewportHeight = visualViewport?.height ?? window.innerHeight
    const viewportRight = viewportLeft + viewportWidth
    const viewportBottom = viewportTop + viewportHeight
    const viewportMargin = 12
    const verticalOffset = 8
    const menuBorderHeight = 2
    const menuWidth = Math.min(240, Math.max(0, viewportWidth - viewportMargin * 2))
    const desiredScrollHeight = Math.min(filteredAssets.value.length * 48, 320)
    const desiredMenuHeight = desiredScrollHeight + menuBorderHeight

    let left = caretRect?.left || editorRect.left
    left = Math.min(
        Math.max(viewportLeft + viewportMargin, left),
        viewportRight - viewportMargin - menuWidth
    )

    const anchorBottom = caretRect?.bottom || editorRect.bottom
    const availableBelow = Math.max(
        0,
        viewportBottom - viewportMargin - anchorBottom - verticalOffset
    )
    const availableAbove = Math.max(
        0,
        editorRect.top - verticalOffset - viewportTop - viewportMargin
    )
    const placeBelow =
        availableBelow >= desiredMenuHeight || availableBelow >= availableAbove
    const availableHeight = placeBelow ? availableBelow : availableAbove
    const menuHeight = Math.min(desiredMenuHeight, availableHeight)
    const scrollMaxHeight = Math.max(0, menuHeight - menuBorderHeight)
    let top = placeBelow
        ? anchorBottom + verticalOffset
        : editorRect.top - verticalOffset - menuHeight
    top = Math.min(
        Math.max(viewportTop + viewportMargin, top),
        viewportBottom - viewportMargin - menuHeight
    )

    mentionMenuPosition.value = { top, left, width: menuWidth, scrollMaxHeight }
}

const closeMentionMenu = () => {
    mentionState.value = null
}

/** 菜单打开时点击编辑器与菜单以外区域则关闭（仅靠 blur 在「先点滚动条失焦」场景下无法再次触发） */
const handlePointerDownOutsideMentionMenu = (event: PointerEvent) => {
    if (!shouldShowMentionMenu.value) return
    const t = event.target
    if (!(t instanceof Node)) return
    const editor = editorRef.value
    const menu = mentionMenuRootRef.value
    if (editor?.contains(t)) return
    if (menu?.contains(t)) return
    closeMentionMenu()
}

const handleMentionMenuEnvironmentChange = (event?: Event) => {
    if (!shouldShowMentionMenu.value) return
    // 列表内部滚动会冒泡/捕获到 document，不能当作「页面滚动关菜单」
    if (event?.type === 'scroll') {
        const t = event.target
        if (t instanceof Node && mentionMenuRootRef.value?.contains(t)) {
            return
        }
    }
    closeMentionMenu()
}

const teardownMentionMenuAutoClose = () => {
    if (typeof window === 'undefined') return

    document.removeEventListener(
        'pointerdown',
        handlePointerDownOutsideMentionMenu,
        true
    )
    window.removeEventListener('resize', handleMentionMenuEnvironmentChange)
    window.visualViewport?.removeEventListener(
        'resize',
        handleMentionMenuEnvironmentChange
    )
    window.visualViewport?.removeEventListener(
        'scroll',
        handleMentionMenuEnvironmentChange
    )
    document.removeEventListener('scroll', handleMentionMenuEnvironmentChange, true)
    editorRef.value?.removeEventListener(
        'scroll',
        handleMentionMenuEnvironmentChange
    )
}

const setupMentionMenuAutoClose = () => {
    if (typeof window === 'undefined') return

    teardownMentionMenuAutoClose()

    document.addEventListener('pointerdown', handlePointerDownOutsideMentionMenu, {
        capture: true,
    })
    window.addEventListener('resize', handleMentionMenuEnvironmentChange, {
        passive: true,
    })
    window.visualViewport?.addEventListener(
        'resize',
        handleMentionMenuEnvironmentChange,
        { passive: true }
    )
    window.visualViewport?.addEventListener(
        'scroll',
        handleMentionMenuEnvironmentChange,
        { passive: true }
    )
    document.addEventListener('scroll', handleMentionMenuEnvironmentChange, true)
    editorRef.value?.addEventListener(
        'scroll',
        handleMentionMenuEnvironmentChange,
        { passive: true }
    )
}

const updateMentionState = (editor: HTMLDivElement) => {
    if (!props.assets?.length || props.disabled) {
        closeMentionMenu()
        return
    }

    const currentTokens = parseEditorTokens(editor)
    const currentValue = serializeAlmightyPromptMentionDisplayTokens(currentTokens, props.assets)
    const context = resolveAlmightyMentionContext(currentValue, getCaretOffset(editor))

    if (!context) {
        closeMentionMenu()
        return
    }

    const prev = mentionState.value
    const sameContext =
        prev &&
        prev.query === context.query &&
        prev.start === context.start &&
        prev.end === context.end

    mentionState.value = {
        query: context.query,
        start: context.start,
        end: context.end,
        activeOption: sameContext ? prev.activeOption : 0,
    }

    nextTick(() => {
        updateMentionMenuPosition(editor)
    })
}

const insertPlainTextAtSelection = (editor: HTMLDivElement, value: string) => {
    const selection = window.getSelection()
    if (!selection) return false

    let range = getSelectionRangeInsideEditor(editor)
    if (!range) {
        editor.focus()
        setCaretAtEditorEnd(editor)
        range = getSelectionRangeInsideEditor(editor)
    }

    if (!range) return false

    range.deleteContents()
    const textNode = document.createTextNode(value)
    range.insertNode(textNode)
    range.setStartAfter(textNode)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
    return true
}

const handleInput = (event: Event) => {
    const editor = event.target as HTMLDivElement
    if (!editor) return

    if (isComposing.value) {
        editor.dataset.empty = getMeaningfulPlainText(editor.textContent || '').length > 0
            ? 'false'
            : 'true'
        return
    }

    syncEditorStateAfterMutation(editor)
}

const handlePaste = (event: ClipboardEvent) => {
    const editor = getEditorElement()
    if (!editor || props.disabled) return

    event.preventDefault()

    const pastedText = event.clipboardData?.getData('text/plain') ?? ''
    const normalizedText = pastedText.replace(/\r\n?/g, '\n')
    if (!normalizedText) return

    if (!insertPlainTextAtSelection(editor, normalizedText)) return
    syncEditorStateAfterMutation(editor)
}

const handleSelectionChange = () => {
    const editor = getEditorElement()
    if (!editor || isComposing.value) return
    updateMentionState(editor)
}

const handleFocus = () => {
    isFocused.value = true
}

const handleBlur = () => {
    isFocused.value = false
    window.setTimeout(() => {
        if (Date.now() < mentionMenuBlurGuardUntil.value) return
        closeMentionMenu()
    }, 120)
}

const handleCompositionEnd = (event: CompositionEvent) => {
    isComposing.value = false
    handleInput(event as unknown as Event)
    if (pendingAssetSync.value) {
        pendingAssetSync.value = false
        nextTick(() => {
            syncEditorForAssetsChange()
        })
    }
}

const handleSelectMention = (asset: AlmightyMentionAsset) => {
    const editor = getEditorElement()
    const state = mentionState.value
    if (!editor || !state) return

    const range = createRangeFromOffsets(editor, state.start, state.end)
    range.deleteContents()

    const chip = createMentionChip({
        type: 'mention',
        assetId: asset.id,
        kind: asset.kind,
        fallbackLabel: asset.label,
    })
    const trailingSpace = document.createTextNode(' ')
    const fragment = document.createDocumentFragment()
    fragment.append(chip, trailingSpace)
    range.insertNode(fragment)

    const selection = window.getSelection()
    if (selection) {
        const nextRange = document.createRange()
        nextRange.setStartAfter(trailingSpace)
        nextRange.collapse(true)
        selection.removeAllRanges()
        selection.addRange(nextRange)
    }

    syncModelsFromEditor(editor)
    closeMentionMenu()

    nextTick(() => {
        editor.focus()
        setCaretByTextOffset(
            editor,
            state.start + asset.label.length + 1
        )
    })
}

const handleKeydown = (event: KeyboardEvent) => {
    if (props.disabled || event.isComposing || isComposing.value) return

    const editor = getEditorElement()
    if (!editor) return

    const isPlainTextInput =
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey

    const incomingLength =
        event.key === 'Enter' ? 1 : getWeightedCharLength(event.key)

    const maxLength = props.maxLength
    if (
        typeof maxLength === 'number' &&
        Number.isFinite(maxLength) &&
        maxLength > 0 &&
        (isPlainTextInput || event.key === 'Enter') &&
        characterCount.value - getSelectionContentLength(editor) + incomingLength > maxLength
    ) {
        event.preventDefault()
        return
    }

    if (event.key === 'Backspace' && removeAdjacentMentionChip(editor, 'backward')) {
        event.preventDefault()
        return
    }

    if (event.key === 'Delete' && removeAdjacentMentionChip(editor, 'forward')) {
        event.preventDefault()
        return
    }

    if (event.key === 'ArrowLeft' && moveCaretAcrossMentionChip(editor, 'left')) {
        event.preventDefault()
        return
    }

    if (event.key === 'ArrowRight' && moveCaretAcrossMentionChip(editor, 'right')) {
        event.preventDefault()
        return
    }

    if (shouldShowMentionMenu.value) {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            if (!mentionState.value) return
            mentionState.value = {
                ...mentionState.value,
                activeOption: Math.min(
                    mentionState.value.activeOption + 1,
                    Math.max(filteredAssets.value.length - 1, 0)
                ),
            }
            return
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault()
            if (!mentionState.value) return
            mentionState.value = {
                ...mentionState.value,
                activeOption: Math.max(mentionState.value.activeOption - 1, 0),
            }
            return
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
            const activeAsset = filteredAssets.value[activeMentionOptionIndex.value]
            if (!activeAsset) return
            event.preventDefault()
            handleSelectMention(activeAsset)
            return
        }

        if (event.key === 'Escape') {
            event.preventDefault()
            closeMentionMenu()
            return
        }
    }

    if (event.key === 'Enter') {
        event.preventDefault()
        const selection = window.getSelection()
        if (!selection || selection.rangeCount === 0) return
        const range = selection.getRangeAt(0)
        range.deleteContents()
        const lineBreak = document.createTextNode('\n')
        range.insertNode(lineBreak)
        range.setStartAfter(lineBreak)
        range.collapse(true)
        selection.removeAllRanges()
        selection.addRange(range)
        syncModelsFromEditor(editor)
        updateMentionState(editor)
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        nextTick(() => {
            const currentEditor = getEditorElement()
            if (currentEditor) updateMentionState(currentEditor)
        })
    }
}

const syncEditorFromModel = (
    options: {
        force?: boolean
        preserveSelection?: boolean
    } = {}
) => {
    const editor = getEditorElement()
    if (!editor) return
    const force = options.force ?? false
    const preserveSelection = options.preserveSelection ?? false
    if (!force && (isFocused.value || isComposing.value)) return

    const currentTokens = parseEditorTokens(editor)
    const nextTokens = cloneAlmightyPromptMentionTokens(normalizedTokens.value)

    if (
        !force &&
        JSON.stringify(currentTokens) === JSON.stringify(nextTokens)
    ) {
        editor.dataset.empty = hasMeaningfulTokenContent(nextTokens) ? 'false' : 'true'
        return
    }

    const selectionSnapshot = preserveSelection
        ? captureSelectionSnapshot(editor)
        : null
    renderTokensToEditor(editor, nextTokens)
    if (selectionSnapshot?.wasFocused && !isComposing.value) {
        nextTick(() => {
            if (typeof window === 'undefined') {
                restoreSelectionSnapshot(editor, selectionSnapshot)
                return
            }
            window.requestAnimationFrame(() => {
                restoreSelectionSnapshot(editor, selectionSnapshot)
            })
        })
    }
}

const syncEditorForAssetsChange = () => {
    const editor = getEditorElement()
    if (!editor) return

    if (isComposing.value) {
        pendingAssetSync.value = true
        return
    }

    const currentTokens = parseEditorTokens(editor)
    const nextTokens = cloneAlmightyPromptMentionTokens(normalizedTokens.value)
    const shouldRerenderForAssets =
        hasMentionToken(currentTokens) || hasMentionToken(nextTokens)

    if (!shouldRerenderForAssets) {
        editor.dataset.empty = hasMeaningfulTokenContent(nextTokens) ? 'false' : 'true'
        if (isFocused.value || document.activeElement === editor) {
            updateMentionState(editor)
        } else {
            closeMentionMenu()
        }
        return
    }

    syncEditorFromModel({
        force: true,
        preserveSelection: isFocused.value || document.activeElement === editor,
    })
}

watch(
    () => [prompt.value, tokens.value],
    () => {
        nextTick(() => {
            syncEditorFromModel()
        })
    },
    { immediate: true, deep: true }
)

watch(
    () => props.assets,
    () => {
        nextTick(() => {
            syncEditorForAssetsChange()
        })
    },
    { deep: true }
)

watch(
    () => props.disabled,
    disabled => {
        if (disabled) {
            closeMentionMenu()
        }
    }
)

watch(
    () => shouldShowMentionMenu.value,
    (visible) => {
        if (visible) {
            nextTick(() => {
                setupMentionMenuAutoClose()
            })
            return
        }

        teardownMentionMenuAutoClose()
    }
)

onMounted(() => {
    nextTick(() => {
        syncEditorFromModel({ force: true })
    })
})

onUnmounted(() => {
    teardownMentionMenuAutoClose()
})
</script>

<style scoped lang="less">
.almighty-mention-editor {
    position: relative;
    width: 100%;
    border-radius: 8px;
    border: none;
    transition: background-color 0.2s ease;

    &.is-focused {
        border: none;
    }

    &.is-disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    &--setting {
        position: relative;
        background: transparent;
        padding: 0;
    }

    &--workbench {
        position: relative;
        background: transparent;
        padding: 0;
    }
}

.almighty-mention-editor__content {
    display: block;
    box-sizing: border-box;
    width: 100%;
    min-height: 96px;
    max-height: 200px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    white-space: pre-wrap;
    word-break: break-word;
    color: #fff;
    font-size: 14px;
    line-height: 24px;
    outline: none;
    background: transparent;
    position: relative;
    z-index: 1;
    cursor: text;

}

.almighty-mention-editor__placeholder {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    color: rgba(255, 255, 255, 0.35);
    font-size: 14px;
    line-height: 24px;
    white-space: pre-wrap;
    word-break: break-word;
    pointer-events: none;
    z-index: 2;
}

.almighty-mention-editor--setting .almighty-mention-editor__content {
    padding: 4px 4px 28px;
}

.almighty-mention-editor--setting .almighty-mention-editor__placeholder {
    top: 4px;
    right: 4px;
    left: 4px;
}

.almighty-mention-editor--workbench .almighty-mention-editor__content {
    padding-right: 0;
    padding-bottom: 0;
}

@media (max-width: 767px) {
    .almighty-mention-editor--workbench .almighty-mention-editor__content {
        padding-bottom: 36px;
    }
}

.almighty-mention-editor__count {
    position: absolute;
    right: 10px;
    bottom: 8px;
    z-index: 2;
    color: rgba(255, 255, 255, 0.55);
    font-size: 12px;
    line-height: 1;
    pointer-events: none;
    transition: color 0.2s ease;

    &.is-near-limit {
        color: #facc15;
    }

    &.is-at-limit {
        color: #f87171;
    }
}

.almighty-mention-editor--workbench .almighty-mention-editor__count {
    position: static;
    display: block;
    width: 100%;
    margin-top: 8px;
    text-align: right;
    z-index: auto;
}

:deep(.almighty-mention-chip) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0 2px;
    padding: 2px 8px 2px 4px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    vertical-align: middle;
    cursor: text;
}

:deep(.almighty-mention-chip__thumb) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.12);
    flex-shrink: 0;
}

:deep(.almighty-mention-chip__preview) {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

:deep(.almighty-mention-chip__audio) {
    color: rgba(255, 255, 255, 0.9);
    font-size: 10px;
    line-height: 1;
}

:deep(.almighty-mention-chip__label) {
    color: rgba(255, 255, 255, 0.92);
    font-size: 12px;
    line-height: 16px;
}

.almighty-mention-menu {
    width: 240px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    background: rgba(10, 10, 12, 0.92);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(20px);
}

/* 高度由定位逻辑按 visualViewport 的上下可用空间计算，素材多时仅在列表内部滚动。 */
.almighty-mention-menu__scroll {
    max-height: var(--almighty-mention-menu-max-height, 320px);
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
}

.almighty-mention-menu__item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    text-align: left;
    transition: background-color 0.2s ease;

    &:hover,
    &.is-active {
        background: rgba(255, 255, 255, 0.12);
    }
}

.almighty-mention-menu__thumb {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    overflow: hidden;
    flex-shrink: 0;
    background: rgba(255, 255, 255, 0.08);
}

.almighty-mention-menu__preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.almighty-mention-menu__audio {
    color: rgba(255, 255, 255, 0.92);
    font-size: 12px;
    line-height: 1;
}

.almighty-mention-menu__label {
    color: rgba(255, 255, 255, 0.92);
    font-size: 14px;
    line-height: 20px;
}
</style>
